import { JSX, ComponentChildren, type RefObject, ComponentChild } from 'preact';
import { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'preact/hooks';
import { normalizePosition } from '../utils/PRIVATE_floatingUtils';
import { Floating, ShiftOptions, RtlSide } from '../UNSAFE_Floating';
import { Layer } from '../UNSAFE_Layer';
import {
  getAutofocusFocusables,
  focusOn,
  focusWithin,
  focusOnElementOrNearestAncestor
} from '../utils/PRIVATE_tabbableUtils';
import { getClientHints } from '../utils/PRIVATE_clientHints';
import { allTabbableElements, getActiveElement } from '../utils/PRIVATE_tabbableUtils';

// Hooks
import { useAnimation } from '../hooks/UNSAFE_useAnimation';
import { DURATION_LARGE } from '../utils/PRIVATE_animationUtils';
import { useUser } from '../hooks/UNSAFE_useUser';
import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';
import { useBreakpointValues } from '../hooks/UNSAFE_useBreakpointValues';
import { useResizeObserver } from '../hooks/UNSAFE_useResizeObserver';

// Style imports
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { classNames } from '../utils/UNSAFE_classNames';
import { DrawerLayoutStyles } from './themes/DrawerLayoutStyles.css';
import { DrawerLayoutRedwoodTheme } from './themes/redwood/DrawerLayoutTheme';

// Types
type IntrinsicProps = Pick<JSX.HTMLAttributes<HTMLDivElement>, 'children'>;
type Placement = 'start' | 'end' | 'bottom';
type Status = 'unmounted' | 'initial' | 'opening' | 'open' | 'closing';

type OnCloseDetail = {
  placement: Placement;
  reason: 'escapeKey';
};
type OnTransitionEndDetail = {
  placement: Placement;
  value: boolean;
};
type DisplayMode = 'overlay' | 'reflow';
type ResolvedDisplayMode = 'full-overlay' | 'overlay' | 'reflow';
type Visibility = 'hidden' | 'visible';

type AnimationEffect =
  | 'expandVertically'
  | 'collapseVertically'
  | 'expandHorizontally'
  | 'collapseHorizontally'
  | 'slideUp'
  | 'slideDown'
  | 'slideOpenLeft'
  | 'slideCloseLeft'
  | 'slideOpenRight'
  | 'slideCloseRight'
  | '';

type Props = IntrinsicProps & {
  /**
   * The content node to be shown within the Start Drawer
   */
  startDrawer?: ComponentChildren;
  /**
   * The content node to be shown within the End Drawer
   */
  endDrawer?: ComponentChildren;
  /**
   * The content node to be shown within the Bottom Drawer
   */
  bottomDrawer?: ComponentChildren;
  /**
   * Specifies whether Start drawer is open.
   */
  isStartOpen?: boolean;
  /**
   * Specifies the display mode of the Start drawer.
   *
   * Supported values are:
   * <p><code>overlay</code>, <code>reflow</code></p>
   * Default is <code>undefined</code>.
   */
  startDisplay?: DisplayMode;
  /**
   * Specifies whether End drawer is open.
   */
  isEndOpen?: boolean;
  /**
   * Specifies the display mode of the End drawer.
   *
   * Supported values are:
   * <p><code>overlay</code>, <code>reflow</code></p>
   * Default is <code>undefined</code>.
   */
  endDisplay?: DisplayMode;
  /**
   * Specifies whether Bottom drawer is open.
   */
  isBottomOpen?: boolean;
  /**
   * Specifies the display mode of the Bottom drawer.
   *
   * Supported values are:
   * <p><code>overlay</code>, <code>reflow</code></p>
   * Default is <code>undefined</code>.
   */
  bottomDisplay?: DisplayMode;
  /**
   * Specifies callback triggered when a user tries to close a Drawer through UI interaction.
   * The parent should listen to this event and close the Drawer. If the parent fails to remove
   * the Popup, then no change will be done in the UI by the component.
   *
   * Supported detail values are:
   * <p><code>placement: start | end | bottom</code></p>
   * <p><code>reason: escapeKey</code></p>
   */
  onClose?: (detail: OnCloseDetail) => void;
  /**
   * Specifies callback triggered after the animation ends.
   *
   * Supported detail values are:
   * <p><code>placement: start | end | bottom</code></p>
   * <p><code>value: boolean</code></p>
   */
  onTransitionEnd?: (detail: OnTransitionEndDetail) => void;
} & TestIdProps;

/**
 * A drawer layout adds expandable side contents (drawers) alongside some primary content.
 *
 * These drawers automatically swap 'reflow' and 'overlay' display mode based on width of the page and can be placed at the 'start', 'end' or 'bottom' edge.
 */
export const DrawerLayout = ({
  children,
  startDrawer,
  endDrawer,
  bottomDrawer,
  isStartOpen = false,
  isEndOpen = false,
  isBottomOpen = false,
  startDisplay,
  endDisplay,
  bottomDisplay,
  onClose,
  onTransitionEnd,
  testId // ...props
}: Props) => {
  // Constants
  const zeroPx = '0px';
  const verticalBreakpoint = 600;
  const stringStart = 'start';
  const stringEnd = 'end';
  const stringBottom = 'bottom';
  const stringInitial = 'initial';
  const stringOpening = 'opening';
  const stringOpen = 'open';
  const stringClosing = 'closing';
  const stringUnmounted = 'unmounted';
  const stringFullOverlay = 'full-overlay';
  const stringOverlay = 'overlay';
  const stringReflow = 'reflow';
  const stringHidden = 'hidden';
  const stringVisible = 'visible';

  // Initialize states
  const [startDrawerStatus, setStartDrawerStatus] = useState<Status>(
    isStartOpen ? stringInitial : stringUnmounted
  );
  const [endDrawerStatus, setEndDrawerStatus] = useState<Status>(
    isEndOpen ? stringInitial : stringUnmounted
  );
  const [bottomDrawerStatus, setBottomDrawerStatus] = useState<Status>(
    isBottomOpen ? stringInitial : stringUnmounted
  );
  const [startVisibility, setStartVisibility] = useState<Visibility>(stringHidden);
  const [endVisibility, setEndVisibility] = useState<Visibility>(stringHidden);
  const [bottomVisibility, setBottomVisibility] = useState<Visibility>(stringHidden);

  const [startOffset, setStartOffset] = useState<number>();
  const [endOffset, setEndOffset] = useState<number>();
  const [bottomOffset, setBottomOffset] = useState<number>();

  // Breakpoints:
  // sm: 0-600px (full-overlay)
  // md: 600-1024px (overlay)
  // lg: >1024px (reflow)
  const displayMode: ResolvedDisplayMode = useBreakpointValues({
    sm: stringFullOverlay,
    md: stringOverlay,
    lg: stringReflow
  });
  const [viewportDisplayModeHorizontal, setViewportDisplayModeHorizontal] =
    useState<ResolvedDisplayMode>(displayMode);
  const [viewportDisplayModeVertical, setViewportDisplayModeVertical] =
    useState<ResolvedDisplayMode>(displayMode);

  // Refs
  const rootRef = useRef<HTMLDivElement>(null);
  const centerRef = useRef<HTMLDivElement>(null);
  const startDrawerRef = useRef<HTMLDivElement>(null);
  const endDrawerRef = useRef<HTMLDivElement>(null);
  const bottomDrawerRef = useRef<HTMLDivElement>(null);
  const placementOfLastlyFocusedRef = useRef<Placement | null>(null);
  const indexOfLastlyFocusedRef = useRef<number | null>(null);
  const stretchedOverlayClippingRef = useRef<HTMLDivElement>(null);

  const startLauncherRef = useRef<HTMLElement | null>(null);
  const endLauncherRef = useRef<HTMLElement | null>(null);
  const bottomLauncherRef = useRef<HTMLElement | null>(null);
  const elementWithFocusBeforeDrawerClosesRef = useRef<HTMLElement | null>(null);

  const [startResolvedDisplayMode, setStartResolvedDisplayMode] =
    useState<ResolvedDisplayMode | null>(null);
  const [endResolvedDisplayMode, setEndResolvedDisplayMode] = useState<ResolvedDisplayMode | null>(
    null
  );
  const [bottomResolvedDisplayMode, setBottomResolvedDisplayMode] =
    useState<ResolvedDisplayMode | null>(null);

  const getDrawerEl = useCallback((placement: Placement) => {
    return getDrawerRef(placement).current;
  }, []);

  const getPlacementOfDrawerWithFocus = useCallback(() => {
    const startDrawerEl = getDrawerEl(stringStart);
    const endDrawerEl = getDrawerEl(stringEnd);
    const bottomDrawerEl = getDrawerEl(stringBottom);
    const activeElement = getActiveElement();
    if (startDrawerEl && startDrawerEl.contains(activeElement)) {
      return stringStart;
    } else if (endDrawerEl && endDrawerEl.contains(activeElement)) {
      return stringEnd;
    } else if (bottomDrawerEl && bottomDrawerEl.contains(activeElement)) {
      return stringBottom;
    } else return null;
  }, [getDrawerEl]);

  const triggerFocusTransferStrategy = useCallback((): void => {
    // Focus strategy - find focused element.
    // Save what drawer and what element had focus before it was eventually conditionally re-rendered

    // In 'auto' display mode we swap 'reflow' and 'overlay' automatically
    // When this change happens, conditional rendering mechanism destroys
    // reflow drawer DOM and creates overlay drawer DOM. This, of course,
    // applies vice versa. In such cases focus gets lost.
    // In DOM destroy cases focus is usually moved to <body>.

    // In standard open cases we trigger focus strategy from animationEnd callback.
    // However, in 'reflow' <> 'overlay' cases animation hook is not used/wanted at all.
    // In addition, we should keep focus on the lastly/customly focused elemenet,
    // e.g. the lastly focused element of a form

    // 1. useBreakpoint hook is triggered even there's no drawer present.
    // Handle focus only if there's at least one drawer open
    if (isStartOpen || isEndOpen || isBottomOpen) {
      // 2. Focus might not be in drawers.
      // In modeless cases user can bring the focus out of open drawers by clicking outside them.

      // Check whether focused element lives in a drawer (one of drawers).
      // Do not handle focus at all if it is not within a drawer.
      // Note: returns null if focus is not within a drawer
      const activeElementPlacement = getPlacementOfDrawerWithFocus();
      if (activeElementPlacement) {
        // Save in which drawer focus lives (save its placement).
        placementOfLastlyFocusedRef.current = activeElementPlacement;

        // 3. Focus was detected in a particular drawer
        // Save index of this element
        const allTabbables = allTabbableElements(
          getDrawerRef(placementOfLastlyFocusedRef.current).current as HTMLDivElement,
          true
        );
        // Save index of lastly focused element
        indexOfLastlyFocusedRef.current = allTabbables.indexOf(getActiveElement() as HTMLElement);
      }
    }
  }, [getPlacementOfDrawerWithFocus, isStartOpen, isBottomOpen, isEndOpen]);

  const isDrawerOpen = useCallback(
    (placement: Placement): boolean => {
      let isOpen = false;
      switch (placement) {
        case stringStart:
          isOpen = isStartOpen;
          break;
        case stringEnd:
          isOpen = isEndOpen;
          break;
        case stringBottom:
          isOpen = isBottomOpen;
          break;
      }
      return isOpen;
    },
    [isStartOpen, isEndOpen, isBottomOpen]
  );

  useEffect((): void => {
    // Focus strategy - apply focus back
    // Return focus to a drawer if applicable
    // 1a. useBreakpoint hook, which monitors viewport resolution, is triggered even drawer(s) are not open.
    // This hook causes changes to 'viewportDisplayModeHorizontal', a dependency of this effect.
    // 1b. This effect can be also triggered by a change to
    // start-display/end-display/bottom-display properties of the DrawerLayout.
    // Make sure that drawer, in which we previously found focus, is still open.
    const placementOflastlyFocusedDrawer = placementOfLastlyFocusedRef.current;
    if (placementOflastlyFocusedDrawer) {
      const drawerRefReceivingFocus: RefObject<HTMLDivElement> = getDrawerRef(
        placementOflastlyFocusedDrawer
      );

      if (drawerRefReceivingFocus.current && isDrawerOpen(placementOflastlyFocusedDrawer)) {
        // 2. Find all tabbables in newly rendered focus receiving focus
        const allTabbables = allTabbableElements(drawerRefReceivingFocus.current, true);

        // 3. Try to focus the same element that had focus in previous display mode
        // In theory drawer's content could be modified.
        // Check whether there are more tababble elements than the index value we saved
        if (
          indexOfLastlyFocusedRef.current != null &&
          allTabbables.length > indexOfLastlyFocusedRef.current
        ) {
          // Focus element using the saved index
          focusOn(allTabbables[indexOfLastlyFocusedRef.current]);
        } else {
          // Focus first tabbable
          focusWithin(drawerRefReceivingFocus.current);
        }

        // Reset saved values to avoid possible side effects
        placementOfLastlyFocusedRef.current = null;
        indexOfLastlyFocusedRef.current = null;
      }
    }
  }, [startResolvedDisplayMode, endResolvedDisplayMode, bottomResolvedDisplayMode, isDrawerOpen]);

  useEffect(() => {
    // Size breakpoint crossed
    triggerFocusTransferStrategy();
    // Reschedules the effect to occur after the ResizeObserver’s loop has completed.
    setTimeout((): void => {
      setViewportDisplayModeHorizontal(displayMode);
    }, 0);
  }, [displayMode, triggerFocusTransferStrategy]);

  const getElementHeight = (element: HTMLDivElement): number => {
    return element ? Math.round(element.offsetHeight) : 0;
  };

  const getElementHeightPx = useCallback((element: HTMLDivElement): string => {
    return `${getElementHeight(element)}px`;
  }, []);

  const getElementWidth = useCallback((element: HTMLDivElement): number => {
    return element ? Math.round(element.getBoundingClientRect().width) : 0;
  }, []);

  const getElementWidthPx = useCallback(
    (element: HTMLDivElement): string => {
      return `${getElementWidth(element)}px`;
    },
    [getElementWidth]
  );

  // Normalizing position
  const { direction } = useUser();
  const isLtr = direction === 'ltr';

  const getNormalizedPlacement = useCallback(
    (placement: Placement): RtlSide => {
      return normalizePosition(placement, direction);
    },
    [direction]
  );

  const isSidePlacement = (placement: Placement): boolean => {
    return [stringStart, stringEnd].indexOf(placement) > -1;
  };

  const isBottomPlacement = (placement: Placement): boolean => {
    return placement === stringBottom;
  };

  const isAnimationPending = (status: Status): boolean => {
    return [stringOpening, stringClosing].indexOf(status) > -1;
  };

  const isDrawerAnimationPending = (placement: Placement): boolean => {
    switch (placement) {
      case stringStart:
        return isAnimationPending(startDrawerStatus);
      case stringEnd:
        return isAnimationPending(endDrawerStatus);
      case stringBottom:
        return isAnimationPending(bottomDrawerStatus);
    }
  };

  const getDrawerVisibility = (placement: Placement): Visibility => {
    switch (placement) {
      case stringStart:
        return startVisibility;
      case stringEnd:
        return endVisibility;
      case stringBottom:
        return bottomVisibility;
    }
  };

  const setDrawerVisibility = (placement: Placement, visibility: Visibility): void => {
    switch (placement) {
      case stringStart:
        return setStartVisibility(visibility);
      case stringEnd:
        return setEndVisibility(visibility);
      case stringBottom:
        return setBottomVisibility(visibility);
    }
  };

  const getDrawerRef = (placement: Placement) => {
    switch (placement) {
      case stringStart:
        return startDrawerRef;
      case stringEnd:
        return endDrawerRef;
      case stringBottom:
        return bottomDrawerRef;
    }
  };

  const getAnimatedNodeRef = (placement: Placement) => {
    switch (placement) {
      case stringStart:
        return startAnimatedNodeRef;
      case stringEnd:
        return endAnimatedNodeRef;
      case stringBottom:
        return bottomAnimatedNodeRef;
    }
  };

  const getDrawerStatus = (placement: Placement) => {
    switch (placement) {
      case stringStart:
        return startDrawerStatus;
      case stringEnd:
        return endDrawerStatus;
      case stringBottom:
        return bottomDrawerStatus;
    }
  };

  const setDrawerStatus = (placement: Placement, status: Status): void => {
    switch (placement) {
      case stringStart:
        setStartDrawerStatus(status);
        break;
      case stringEnd:
        setEndDrawerStatus(status);
        break;
      case stringBottom:
        setBottomDrawerStatus(status);
        break;
    }
  };

  const resetAnimatedNodeRef = (placement: Placement): void => {
    switch (placement) {
      case stringStart:
        startAnimatedNodeRef(null);
        break;
      case stringEnd:
        endAnimatedNodeRef(null);
        break;
      case stringBottom:
        bottomAnimatedNodeRef(null);
        break;
    }
  };

  const getDrawerDisplayProp = useCallback(
    (placement: Placement) => {
      switch (placement) {
        case stringStart:
          return startDisplay;
        case stringEnd:
          return endDisplay;
        case stringBottom:
          return bottomDisplay;
      }
    },
    [startDisplay, endDisplay, bottomDisplay]
  );

  const getDrawerResolvedDisplayMode = useCallback(
    (placement: Placement) => {
      const displayProp = getDrawerDisplayProp(placement);
      if (displayProp) {
        return displayProp;
      } else {
        if (isSidePlacement(placement)) {
          return viewportDisplayModeHorizontal;
        }
        return viewportDisplayModeVertical;
      }
    },
    [getDrawerDisplayProp, viewportDisplayModeHorizontal, viewportDisplayModeVertical]
  );

  // Animation effects config
  const getAnimationStates = (ref: RefObject<HTMLElement>) => {
    return {
      expandVertically: {
        from: {
          maxHeight: zeroPx
        },
        to: {
          maxHeight: '100%'
        },
        options: {
          duration: DURATION_LARGE
        }
      },
      collapseVertically: {
        from: {
          maxHeight: '100%'
        },
        to: {
          maxHeight: zeroPx
        },
        options: {
          duration: DURATION_LARGE
        }
      },
      expandHorizontally: () => {
        return {
          from: {
            maxWidth: zeroPx
          },
          to: {
            // We need a function here as the width is dynamic
            // and percentage can't be used in reflow mode
            maxWidth: getElementWidthPx(ref.current as HTMLDivElement)
          },
          options: {
            duration: DURATION_LARGE
          }
        };
      },
      collapseHorizontally: () => {
        return {
          from: {
            // We need a function here as the width is dynamic
            // and percentage can't be used in reflow mode
            maxWidth: getElementWidthPx(ref.current as HTMLDivElement)
          },
          to: {
            maxWidth: zeroPx
          },
          options: {
            duration: DURATION_LARGE
          }
        };
      },
      slideUp: {
        from: {
          // We need a function here as the height is dynamic
          // and percentage can't be used in reflow mode
          translateY: '100%'
        },
        to: {
          translateY: zeroPx
        },
        options: {
          duration: DURATION_LARGE
        }
      },
      slideDown: {
        from: {
          translateY: zeroPx
        },
        to: {
          translateY: '100%'
        },
        options: {
          duration: DURATION_LARGE
        }
      },
      slideOpenLeft: {
        from: {
          translateX: '-100%'
        },
        to: {
          translateX: zeroPx
        },
        options: {
          duration: DURATION_LARGE
        }
      },
      slideCloseLeft: {
        from: {
          translateX: '0'
        },
        to: {
          translateX: '-100%'
        },
        options: {
          duration: DURATION_LARGE
        }
      },
      slideOpenRight: {
        from: {
          translateX: '100%'
        },
        to: {
          translateX: zeroPx
        },
        options: {
          duration: DURATION_LARGE
        }
      },
      slideCloseRight: {
        from: {
          translateX: '0'
        },
        to: {
          translateX: '100%'
        },
        options: {
          duration: DURATION_LARGE
        }
      }
    };
  };

  const removeAnimationInlineStyles = (placement: Placement): void => {
    // Remove style attribute left on the element by the Animation util
    const drawerRef: RefObject<HTMLDivElement> = getDrawerRef(placement);
    drawerRef.current?.removeAttribute('style');
  };

  const setInitialFocus = (placement: Placement): void => {
    // Set focus to the first match:
    const drawerRef: RefObject<HTMLDivElement> = getDrawerRef(placement);

    if (!drawerRef.current) {
      return;
    }
    // 1. First element inside the drawer matching [autofocus]
    const autofocusItems: HTMLElement[] = getAutofocusFocusables(drawerRef.current);
    if (autofocusItems.length > 0) {
      focusOn(autofocusItems[0]);
      return;
    }
    // 2. Tabbable element inside the content element
    // 3. The drawer itself
    focusWithin(drawerRef.current);
  };

  const getLauncherRef = (placement: Placement) => {
    switch (placement) {
      case stringStart:
        return startLauncherRef;
      case stringEnd:
        return endLauncherRef;
      case stringBottom:
        return bottomLauncherRef;
    }
  };

  const returnFocusToLauncher = (placement: Placement): void => {
    // Return the focus to the drawer's launcher only if the focus
    // is currently living within a drawer.
    // As we do not apply focus-trap in modeless mode, user is able to
    // get the focus out of the drawer. In this case we do not want to handle it.
    if (getDrawerRef(placement).current?.contains(elementWithFocusBeforeDrawerClosesRef.current)) {
      focusOnElementOrNearestAncestor(getLauncherRef(placement).current as HTMLElement);
    }
  };

  // Animation effect
  const getAnimationEffect = (placement: Placement) => {
    const bottomPlacement = isBottomPlacement(placement);
    const status = getDrawerStatus(placement);
    if (getDrawerResolvedDisplayMode(placement) === stringReflow) {
      // Reflow display mode
      // Effect: Expand/Collapse
      if (bottomPlacement) {
        // useAnimation hook ignores to apply effect if it matches the previous one.
        // if (currentAnimationState.current === animationState) return;
        // That's why we need to pair particular with particualar status and
        // send empty stirng in all other cases
        if (status === 'opening') {
          return 'expandVertically';
        }
        if (status === 'closing') {
          return 'collapseVertically';
        }
        return '';
      } else {
        if (status === 'opening') {
          return 'expandHorizontally';
        }
        if (status === 'closing') {
          return 'collapseHorizontally';
        }
        return '';
      }
    } else {
      // Overlay display mode
      // Effect: SlideIn/SlideOut
      if (bottomPlacement) {
        if (status === 'opening') {
          return 'slideUp';
        }
        if (status === 'closing') {
          return 'slideDown';
        }
        return '';
      } else {
        if (status === 'opening') {
          return getNormalizedPlacement(placement) === 'left' ? 'slideOpenLeft' : 'slideOpenRight';
        }
        if (status === 'closing') {
          return getNormalizedPlacement(placement) === 'left'
            ? 'slideCloseLeft'
            : 'slideCloseRight';
        }
        return '';
      }
    }
  };

  // Custom hook to be shared for each side
  // It can't be a function as a hook can only be called within a custom hook
  function useNodeRef(placement: Placement, ref: RefObject<HTMLElement>) {
    const isOpen: boolean = isDrawerOpen(placement);
    const status = getDrawerStatus(placement);
    return useAnimation<AnimationEffect, HTMLDivElement>(getAnimationEffect(placement), {
      animationStates: getAnimationStates(ref),
      isAnimatedOnMount: true,
      onAnimationEnd: () => {
        if (status === stringOpening) {
          if (isOverlay(placement)) {
            setInitialFocus(placement);
          }
          // Remove animation's inline style
          removeAnimationInlineStyles(placement);

          // In case drawer display mode changed at runtime (reflow <> overlay)
          // we need to reset animation node ref otherwise animation state is not refelected.
          // Case:
          // <oj-c-drawer-layout> opens on wide screen by default in reflow mode (flex layout).
          // useAnimation effect was 'expand'
          // Viewport is shrinked under 1024px, display mode changes to overlay.
          // This means DOM is reparented.
          // For overlay mode, closing animation effect should be 'slideout'
          // If animation nodeRef is not reset, closing effect is neither 'collapse' nor 'slideout'.
          resetAnimatedNodeRef(placement);

          setDrawerStatus(placement, stringOpen);
          onTransitionEnd?.({
            placement: placement,
            value: true
          });
        } else if (!isOpen) {
          if (isOverlay(placement)) {
            returnFocusToLauncher(placement);
          }
          resetAnimatedNodeRef(placement);
          setDrawerVisibility(placement, stringHidden);
          setDrawerStatus(placement, stringUnmounted);
          onTransitionEnd?.({
            placement: placement,
            value: false
          });
        }
      }
    }).nodeRef;
  }

  // Drawer refs
  const startAnimatedNodeRef = useNodeRef(stringStart, startDrawerRef);
  const endAnimatedNodeRef = useNodeRef(stringEnd, endDrawerRef);
  const bottomAnimatedNodeRef = useNodeRef(stringBottom, bottomDrawerRef);

  //testId support
  const testIdProps = useTestId(testId);

  const setLauncherRef = (placement: Placement) => {
    const activeElement = getActiveElement() as HTMLElement;
    switch (placement) {
      case stringStart:
        startLauncherRef.current = activeElement;
        break;
      case stringEnd:
        endLauncherRef.current = activeElement;
        break;
      case stringBottom:
        bottomLauncherRef.current = activeElement;
        break;
    }
  };

  // Custom hook to be shared for each side
  // It can't be a function as a hook can only be called within a custom hook
  function useDrawerStatus(placement: Placement): void {
    const isOpen = isDrawerOpen(placement);
    const drawerStatus = getDrawerStatus(placement);

    useEffect(() => {
      // Ignore initial render
      // 1. Ignore status update on initial render
      if (drawerStatus === stringUnmounted && !isOpen) {
        return;
      }

      // 2. Unmounted
      // Mount visually hidden component.
      // Component gets mounted, but will be visually hidden to avoid flickering.
      // Flickering happens because it gets rendered before animation hook repositions
      // it in next render cycle
      if (drawerStatus === stringUnmounted && isOpen) {
        setDrawerStatus(placement, stringInitial);
        return;
      }

      // 3. Initial
      // Setup animation
      // Component has been mounted.
      // We will kick out animation as node is already present in DOM.
      // To avoid flickering, we still keep it visually hidden until the next render cycle.
      if (drawerStatus === stringInitial && isOpen) {
        if (isOverlay(placement)) {
          const drawerRef = getDrawerRef(placement);
          const animatedNodeRef = getAnimatedNodeRef(placement);
          animatedNodeRef(drawerRef.current);
          setDrawerStatus(placement, stringOpening);
          return;
        }
      }

      // 4. Opening
      // Unhide component. It becomes visible.
      if (drawerStatus === stringOpening && isOpen) {
        if (isOverlay(placement)) {
          setDrawerVisibility(placement, stringVisible);
          // Remember launcher
          setLauncherRef(placement);
          return;
        }
      }

      // 5. Open
      // Status 'open' is set in 'onAnimationEnd' handler

      // 6. Closing
      if (!isOpen) {
        // As animatedNodeRef was reset in onAnimationEnd we need to set it again
        // see useAnimation transition end for details
        const drawerRef = getDrawerRef(placement);
        const animatedNodeRef = getAnimatedNodeRef(placement);
        animatedNodeRef(drawerRef.current);

        elementWithFocusBeforeDrawerClosesRef.current = getActiveElement() as HTMLElement;
        setDrawerStatus(placement, stringClosing);
      }
    }, [isOpen, drawerStatus, placement]);

    // For reflow only
    useLayoutEffect(() => {
      // 3. Initial
      // Setup animation
      // Component has been mounted.
      // We will kick out animation as node is already present in DOM.
      // To avoid flickering, we still keep it visually hidden until the next render cycle.
      if (drawerStatus === stringInitial && isOpen) {
        if (!isOverlay(placement)) {
          const drawerRef = getDrawerRef(placement);
          const animatedNodeRef = getAnimatedNodeRef(placement);
          animatedNodeRef(drawerRef.current);
          setDrawerStatus(placement, stringOpening);
          return;
        }
      }

      // 4. Opening
      // Unhide component. It becomes visible.
      if (drawerStatus === stringOpening && isOpen) {
        if (!isOverlay(placement)) {
          setDrawerVisibility(placement, stringVisible);
          // Remember launcher
          setLauncherRef(placement);
          return;
        }
      }
    }, [drawerStatus, isOpen, placement]);
  }

  // Drawers' status handler±
  useDrawerStatus(stringStart);
  useDrawerStatus(stringEnd);
  useDrawerStatus(stringBottom);

  // Keydown handlers
  const handleKeyDownStart = (event: KeyboardEvent): void => {
    handleKeyDown(stringStart, event);
  };
  const handleKeyDownEnd = (event: KeyboardEvent): void => {
    handleKeyDown(stringEnd, event);
  };
  const handleKeyDownBottom = (event: KeyboardEvent): void => {
    handleKeyDown(stringBottom, event);
  };

  const handleKeyDown = (placement: Placement, event: KeyboardEvent): void => {
    if (event.code === 'Escape') {
      // Handle only in overlay mode
      if (isOverlay(placement)) {
        onClose?.({
          placement,
          reason: 'escapeKey'
        });
      }
    }
  };

  // Styles
  const { classes, styles } = useComponentTheme<never, DrawerLayoutStyles>(
    DrawerLayoutRedwoodTheme
  );

  const getVisibilityClass = (visibility: Visibility) => {
    return visibility === stringHidden ? styles.visibilityHidden : styles.visibilityVisible;
  };

  const isStretched = useCallback((): boolean => {
    return displayMode === stringFullOverlay;
  }, [displayMode]);

  const isOverlay = useCallback(
    (placement: Placement): boolean => {
      return (
        [stringFullOverlay, stringOverlay].indexOf(getDrawerResolvedDisplayMode(placement)) > -1
      );
    },
    [getDrawerResolvedDisplayMode]
  );

  const isFullOverlay = (placement: Placement): boolean => {
    return getDrawerResolvedDisplayMode(placement) === stringFullOverlay;
  };

  const getDrawerClassNames = (placement: Placement): string => {
    // Visibility style is always added
    const visibility: Visibility = getDrawerVisibility(placement);
    const classNamesArr: string[] = [getVisibilityClass(visibility)];

    // Styles for overlay vs. full-overlay vs. reflow
    if (isOverlay(placement)) {
      classNamesArr.push(styles.overlayDrawer);
      if (isSidePlacement(placement) && !isFullOverlay(placement)) {
        classNamesArr.push(styles.overlaySideDrawerNonFullWidth);
      }

      // Styles for stretched overlay drawer
      if (isStretched() && isSidePlacement(placement)) {
        classNamesArr.push(styles.stretchedOverlaySideDrawer);
      }
    } else {
      classNamesArr.push(styles.reflowDrawer);
    }

    // Styles for animation
    if (isDrawerAnimationPending(placement)) {
      classNamesArr.push(styles.animatedDrawer);
    }

    const getPlacementSpecificStyles = (placement: Placement): string => {
      switch (placement) {
        case stringStart:
          if (isOverlay(stringStart)) {
            return isLtr ? styles.overlayDrawerLeft : styles.overlayDrawerRight;
          } else {
            return isLtr ? styles.reflowDrawerLeft : styles.reflowDrawerRight;
          }
        case stringEnd:
          if (isOverlay(stringEnd)) {
            return isLtr ? styles.overlayDrawerRight : styles.overlayDrawerLeft;
          } else {
            return isLtr ? styles.reflowDrawerRight : styles.reflowDrawerLeft;
          }
        case stringBottom:
          return isOverlay(stringBottom) ? styles.overlayDrawerBottom : styles.reflowDrawerBottom;
      }
    };
    // Placement specific styles
    classNamesArr.push(getPlacementSpecificStyles(placement));

    return classNames(classNamesArr);
  };

  const getDrawerProp = (placement: Placement) => {
    switch (placement) {
      case stringStart:
        return startDrawer;
      case stringEnd:
        return endDrawer;
      case stringBottom:
        return bottomDrawer;
    }
  };

  const getKeyDownHandler = (placement: Placement) => {
    switch (placement) {
      case stringStart:
        return handleKeyDownStart;
      case stringEnd:
        return handleKeyDownEnd;
      case stringBottom:
        return handleKeyDownBottom;
    }
  };

  const renderOverlayDrawer = (placement: Placement) => {
    if (isStretched() && isSidePlacement(placement)) {
      const clippingAreaStyles = [styles.stretchedOverlaySideDrawerClippingArea];
      switch (placement) {
        case stringStart:
          clippingAreaStyles.push(
            isLtr
              ? styles.stretchedOverlaySideDrawerClippingAreaLeft
              : styles.stretchedOverlaySideDrawerClippingAreaRight
          );
          break;
        case stringEnd:
          clippingAreaStyles.push(
            isLtr
              ? styles.stretchedOverlaySideDrawerClippingAreaRight
              : styles.stretchedOverlaySideDrawerClippingAreaLeft
          );
          break;
      }

      // Add clipping div
      return (
        <div ref={stretchedOverlayClippingRef} className={classNames(clippingAreaStyles)}>
          {renderDrawer(placement)}
        </div>
      );
    } else {
      // Overlay bottom drawer or not stretched side drawer
      return renderDrawer(placement);
    }
  };

  // Single drawer template
  const renderDrawer = (placement: Placement) => {
    return (
      <div
        ref={getDrawerRef(placement)}
        className={getDrawerClassNames(placement)}
        tabIndex={isOverlay(placement) ? -1 : undefined}
        onKeyDown={getKeyDownHandler(placement)}>
        {getDrawerProp(placement)}
      </div>
    );
  };

  const clientHints = getClientHints();

  const getViewportHeight = useCallback((): number => {
    if (clientHints.platform === 'ios') {
      // On ios window.innerHeight is not recommended way of measuring the viewport
      return document.documentElement.clientHeight;
    }
    return window.innerHeight;
  }, [clientHints.platform]);

  // We faced error in KM test environment
  // Error: ResizeObserver loop completed with undelivered notifications.
  // This happens when an observed element is resized again during a notification.
  // Another cause can be rounding values and then setting the rounded value back.
  // If this is in the same loop as the resize notification, it will cause another resize on the element.
  // Wrapping ResizeObserver’s callback in a setTimeout with a delay of 0 milliseconds
  // reschedules the effect to occur after the ResizeObserver’s loop has completed.
  const rescheduleObserverHandler = (callback: () => void) => {
    setTimeout(() => {
      callback();
    }, 0);
  };

  const handleRescheduledVerticalResize = useCallback(() => {
    const viewportHeight = getViewportHeight();
    if (viewportHeight >= verticalBreakpoint) {
      if (viewportDisplayModeVertical != stringReflow) {
        triggerFocusTransferStrategy();
        setViewportDisplayModeVertical(stringReflow);
      }
    } else {
      if (viewportDisplayModeVertical != stringOverlay) {
        triggerFocusTransferStrategy();
        setViewportDisplayModeVertical(stringOverlay);
      }
    }
  }, [viewportDisplayModeVertical, getViewportHeight, triggerFocusTransferStrategy]);

  const handleVerticalResize = useCallback(() => {
    rescheduleObserverHandler(handleRescheduledVerticalResize);
  }, [handleRescheduledVerticalResize]);

  // Window resize listener
  // useBreakpoint hook is designed for horizontal breakpoints only
  // This listener is used for 600px (overlay vs. reflow) breakpoint
  // used for bottom drawer
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      handleVerticalResize();
    });
    resizeObserver.observe(document.body);
    // Deregister resize observer when Drawer layout unmounts
    return () => {
      resizeObserver.unobserve(document.body);
    };
  }, [handleVerticalResize]);

  const measureOffset = (placement: Placement): number => {
    const drawerEl = getDrawerEl(placement);
    if (drawerEl) {
      return isSidePlacement(placement) ? -getElementWidth(drawerEl) : -getElementHeight(drawerEl);
    }
    return 0;
  };

  const getOffsetState = (placement: Placement) => {
    switch (placement) {
      case stringStart:
        return startOffset;
      case stringEnd:
        return endOffset;
      case stringBottom:
        return bottomOffset;
    }
  };

  const setSideOverlayDrawerHeight = useCallback(
    (placement: Placement) => {
      // By default PopupElements have no predefined height.
      // In 'overlay' mode drawers should be
      // of the same height as their parent <oj-drawer-layout>
      const centerSectionHeight = getElementHeightPx(centerRef.current as HTMLDivElement);

      const drawerEl = getDrawerRef(placement).current;
      if (drawerEl && !isStretched() && isDrawerOpen(placement) && isOverlay(placement)) {
        drawerEl.style.height = centerSectionHeight;
      }
    },
    [getElementHeightPx, isDrawerOpen, isOverlay, isStretched]
  );

  const handleSideDrawerFullWidth = (placement: Placement) => {
    const drawerEl = getDrawerRef(placement).current;
    if (drawerEl) {
      if (isDrawerOpen(placement)) {
        const drawerInlineStyle = drawerEl.style;
        if (isFullOverlay(placement)) {
          drawerInlineStyle.width = getElementWidthPx(rootRef.current as HTMLDivElement);
        } else {
          drawerInlineStyle.removeProperty('width');
        }
      }
    }
  };

  const setBottomOverlayDrawerWidth = useCallback(() => {
    const bottomDrawerElement = bottomDrawerRef.current;
    if (bottomDrawerElement && isDrawerOpen(stringBottom) && isOverlay(stringBottom)) {
      const centerSectionWidth = getElementWidthPx(centerRef.current as HTMLDivElement);
      bottomDrawerElement.style.width = centerSectionWidth;
    }
  }, [getElementWidthPx, isDrawerOpen, isOverlay]);

  useEffect(() => {
    // Stretched case - set side drawer size
    const start: Placement = 'start';
    const end: Placement = 'end';
    [start, end].forEach((placement: Placement) => {
      if (isDrawerOpen(placement) && isStretched()) {
        // When opening the drawer its ref and its clipping area
        // are not present on the first run of this effect.
        // That's why we are using question mark when manipulating their styles.

        // Remove residual inline style when changing overlayHeight
        // from 'inset' to 'stretch' at Runtime
        // In 'inset' mode we calculate the height to fit the parent container.
        getDrawerEl(placement)?.style.removeProperty('height');

        // Do the same removal also on the wrapping clipping area.
        // DrawerLayout component is not setting this style. This is probably added by Floating.
        const clippingArea = getDrawerEl(placement)?.parentNode as HTMLElement;
        clippingArea?.style.removeProperty('height');
      }
    });
  }, [isDrawerOpen, getDrawerEl, getElementWidth, isStretched]);

  const setDrawerSize = (placement: Placement) => {
    // Not stretched overlay drawer
    if (isSidePlacement(placement)) {
      setSideOverlayDrawerHeight(placement);
      handleSideDrawerFullWidth(placement);
    }
    if (isBottomPlacement(placement)) {
      setBottomOverlayDrawerWidth();
    }
  };

  // DrawerLayout resize observer
  const handleDrawerLayoutResize = () => {
    rescheduleObserverHandler(handleRescheduledDrawerLayoutResize);
  };
  const handleRescheduledDrawerLayoutResize = useCallback(() => {
    // If the DrawerLayout (page content) changes size,
    // adjust OVERLAY side drawers (start/end) height to fit the drawer
    // Note that these two functions checks that 'overlay' condition is fulfilled
    if (!isStretched()) {
      setSideOverlayDrawerHeight(stringStart);
      setSideOverlayDrawerHeight(stringEnd);
    }
    setBottomOverlayDrawerWidth();
  }, [setSideOverlayDrawerHeight, setBottomOverlayDrawerWidth, isStretched]);
  useResizeObserver(rootRef, handleDrawerLayoutResize);

  const setOffsetState = (placement: Placement) => {
    // This method sets offset used only in overlay mode
    if (isOverlay(placement)) {
      const offset = measureOffset(placement);
      switch (placement) {
        case stringStart:
          // Fixed strategy calculates position from left to right.
          // Stretched drawer needs no offset
          setStartOffset(isStretched() ? 0 : offset);
          break;
        case stringEnd:
          // Fixed strategy calculates position from left to right.
          // Stretched drawer needs no offset
          setEndOffset(isStretched() ? offset - 20 : offset);
          break;
        case stringBottom:
          setBottomOffset(offset);
          break;
      }
    }
  };

  // Start drawer resize observer
  const handleStartDrawerResize = () => {
    rescheduleObserverHandler(handleRescheduledStartDrawerResize);
  };
  const handleRescheduledStartDrawerResize = () => {
    setOffsetState(stringStart);
    setBottomOverlayDrawerWidth();
  };
  useResizeObserver(startDrawerRef, handleStartDrawerResize);

  // End drawer resize observer
  const handleEndDrawerResize = () => {
    rescheduleObserverHandler(handleRescheduledEndDrawerResize);
  };
  const handleRescheduledEndDrawerResize = () => {
    setOffsetState(stringEnd);
    setBottomOverlayDrawerWidth();
  };
  useResizeObserver(endDrawerRef, handleEndDrawerResize);

  // Bottom drawer resize observer
  const handleBottomDrawerResize = () => {
    rescheduleObserverHandler(handleRescheduledBottomDrawerResize);
  };
  const handleRescheduledBottomDrawerResize = () => {
    setOffsetState(stringBottom);
  };
  useResizeObserver(bottomDrawerRef, handleBottomDrawerResize);

  const getDrawerPreviousResolvedDisplayMode = (placement: Placement) => {
    switch (placement) {
      case stringStart:
        return startResolvedDisplayMode;
      case stringEnd:
        return endResolvedDisplayMode;
      case stringBottom:
        return bottomResolvedDisplayMode;
    }
  };

  const saveResolvedDisplayMode = (placement: Placement) => {
    const currentResolvedDisplayMode = getDrawerResolvedDisplayMode(placement);
    if (getDrawerPreviousResolvedDisplayMode(placement) != currentResolvedDisplayMode) {
      switch (placement) {
        case stringStart:
          setStartResolvedDisplayMode(currentResolvedDisplayMode);
          break;
        case stringEnd:
          setEndResolvedDisplayMode(currentResolvedDisplayMode);
          break;
        case stringBottom:
          setBottomResolvedDisplayMode(currentResolvedDisplayMode);
          break;
      }

      // In case of 'reflow <> overlay' change conditional rendering of a drawer happens.
      // The old DOM is destoryed and a new DOM is created. In this case focus gets lost.
      // Browser moves the focus to body.
      // We try to prevent it by eventually applying focus in the newly created DOM.
      // If at least one drawer is open, we will check whether focus lives whithin.
      // See the following method for more details.
      triggerFocusTransferStrategy();
    }
  };

  const getReflowOrOverlayDrawer = (placement: Placement) => {
    const shiftOptions: ShiftOptions = {
      mainAxis: false,
      crossAxis: false
    };

    const flipOptions = {
      mainAxis: false,
      crossAxis: false
    };

    // Save resolved display mode of a drawer so that we can compare it with 'next' value
    saveResolvedDisplayMode(placement);

    // Reflow vs. overlay drawer
    if (isOverlay(placement)) {
      setDrawerSize(placement);

      // Initial offset
      if (!getOffsetState(placement)) {
        setOffsetState(placement);
      }

      return (
        // Reconciliation issue. Reparented node must
        // be a single child of a <div>
        <div>
          <Layer>
            <Floating
              anchorRef={placement === stringBottom ? centerRef : rootRef}
              placement={placement}
              class={isDrawerAnimationPending(placement) ? styles.floating : ''}
              strategy={isStretched() && isSidePlacement(placement) ? 'fixed' : 'absolute'}
              offsetValue={getOffsetState(placement)}
              flipOptions={flipOptions}
              shiftOptions={shiftOptions}>
              {renderOverlayDrawer(placement)}
            </Floating>
          </Layer>
        </div>
      );
    } else {
      // Reflow display mode - flex layout
      return renderDrawer(placement);
    }
  };

  // React calculates how your component should look and prepares the updates
  // React applies those updates to the DOM, making the changes ready for display
  //   useLayoutEffect hook runs right after the DOM is updated but before the screen shows anything. it lets you make adjustments here that will be applied before the screen updates
  // the browser then paints the updated content to the screen
  // useEffect hook runs after the screen updates, so any changes here will cause another re-render but only after the user has already seen the initial update

  // Render drawer conditionally
  const getDrawer = (placement: Placement): ComponentChild => {
    const isMounted = getDrawerStatus(placement) !== stringUnmounted;
    return isMounted ? getReflowOrOverlayDrawer(placement) : null;
  };

  return (
    <div ref={rootRef} className={classes} {...testIdProps}>
      {/*Start drawer*/}
      {getDrawer(stringStart)}
      {/*Center section*/}
      <div ref={centerRef} className={styles.centerSection}>
        <div className={styles.mainContent}>
          {/*<main content*/}
          {children}
        </div>
        {/*Bottom drawer*/}
        {getDrawer(stringBottom)}
      </div>
      {/*End drawer*/}
      {getDrawer(stringEnd)}
    </div>
  );
};
